import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/dist/query/react';
import qs from 'query-string';

import getEnvConfig from '../../../../../utils/config/env';
import {
    AttributesFilters,
    AttributesPayload,
    AttributesProps,
} from './attributes.interface';
import { PaginatedResponse } from '../../../../communities/interfaces/PaginatedResponse';

export const attributesApi = createApi({
    reducerPath: 'attributes',
    baseQuery: fetchBaseQuery({
        baseUrl: getEnvConfig().env.API_URL,
        prepareHeaders: headers => {
            const token = localStorage.getItem('accessToken');
            headers.set('Authorization', `Bearer ${token}`);
            return headers;
        },
    }),
    tagTypes: ['Attributes'],

    endpoints: builder => ({
        createDisputeAI: builder.mutation<
            AttributesProps[],
            { shop_id: number }
        >({
            query: payload => ({
                url: `/shops/${payload.shop_id}/attributes/set-recommended`,
                method: 'POST',
                body: { ...payload },
            }),
            transformResponse: (response: { data: AttributesProps[] }) => {
                return response.data;
            },
        }),

        createPosition: builder.mutation<AttributesProps[], AttributesPayload>({
            query: payload => ({
                url: `/shops/${payload.shop_id}/attributes/${payload.id}/set-position`,
                method: 'POST',
                body: { ...payload },
            }),
            transformResponse: (response: { data: AttributesProps[] }) => {
                return response.data;
            },
            invalidatesTags: ['Attributes'],
        }),

        createVisibility: builder.mutation<
            AttributesProps[],
            AttributesPayload
        >({
            query: payload => ({
                url: `/shops/${payload.shop_id}/attributes/${payload.id}/set-visibility`,
                method: 'POST',
                body: { ...payload },
            }),
            transformResponse: (response: { data: AttributesProps[] }) => {
                return response.data;
            },
            invalidatesTags: ['Attributes'],
        }),

        readAttributes: builder.query<
            PaginatedResponse<AttributesProps>,
            AttributesFilters
        >({
            query: payload => {
                const queryArgs = qs.stringify(payload, {
                    arrayFormat: 'bracket-separator',
                    skipNull: true,
                });
                const params = new URLSearchParams(queryArgs);
                params.delete('id');
                const result = params.toString();
                return {
                    url: `/shops/${payload.id}/attributes?${result}`,
                };
            },
            transformResponse: (
                res: {
                    data: AttributesProps[];
                    payload?: { is_ai_recommends?: boolean };
                },
                meta
            ) => {
                return {
                    items: res.data || [],
                    total:
                        Number(meta.response.headers.get('Items-Count')) || 0,
                    is_ai_recommends: res.payload?.is_ai_recommends ?? false,
                };
            },
            providesTags: ['Attributes'],
        }),
    }),
});

export const {
    useCreateDisputeAIMutation,
    useLazyReadAttributesQuery,
    useCreateVisibilityMutation,
    useCreatePositionMutation,
} = attributesApi;
